<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { Build } from '@kilnci/shared';

const props = defineProps<{
  build: Build | null;
}>();

const requires = computed<Record<string, string>>(
  () => props.build?.labels.requires ?? {},
);

const hasRequires = computed(() => Object.keys(requires.value).length > 0);

const waiting = computed(() => {
  const b = props.build;
  if (!b) return false;
  return b.status === 'pending' && b.workerId === null && hasRequires.value;
});

// The scheduler prefixes `error_message` with `no_eligible_worker:` when
// maintenance() times a starved build out — keying off that sentinel
// avoids string matching the human-readable suffix.
const failed = computed(() => {
  const b = props.build;
  return (
    b?.status === 'failed' &&
    typeof b.errorMessage === 'string' &&
    b.errorMessage.startsWith('no_eligible_worker:')
  );
});

const requiresQueryString = computed(() =>
  Object.entries(requires.value)
    .map(([k, v]) => `${k}=${v}`)
    .join(','),
);
</script>

<template>
  <div v-if="waiting" class="dispatch-banner waiting">
    <strong>Waiting for an eligible worker.</strong>
    This build will only dispatch onto a worker whose labels contain every
    <code>key=value</code> below — and, if that worker is tainted, whose
    tainted labels are all listed here.
    <RouterLink
      :to="{ path: '/workers', query: { requires: requiresQueryString } }"
      class="inline-link"
    >
      See matching workers →
    </RouterLink>
    <div class="labels-list inline">
      <span v-for="(value, key) in requires" :key="key" class="label-pill">
        {{ key }}=<strong>{{ value }}</strong>
      </span>
    </div>
  </div>

  <div v-else-if="failed" class="dispatch-banner failed">
    <strong>No eligible worker available.</strong>
    This build timed out waiting for a worker matching its
    requirements. Either adjust the pipeline's
    <code>labels.requires</code> or bring online a worker that
    advertises these labels.
    <RouterLink
      :to="{ path: '/workers', query: { requires: requiresQueryString } }"
      class="inline-link"
    >
      See matching workers →
    </RouterLink>
    <div class="labels-list inline">
      <span v-for="(value, key) in requires" :key="key" class="label-pill">
        {{ key }}=<strong>{{ value }}</strong>
      </span>
    </div>
  </div>
</template>

<style scoped>
.dispatch-banner {
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  border: 1px solid transparent;
}

.dispatch-banner.waiting {
  background: var(--color-info-bg);
  color: var(--color-info-text);
  border-color: var(--color-info-text);
}

.dispatch-banner.failed {
  background: var(--color-error-bg);
  color: var(--color-error-text);
  border-color: var(--color-error-text);
}

.dispatch-banner code {
  font-size: 0.8em;
  padding: 1px 4px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.08);
}

.inline-link {
  color: inherit;
  text-decoration: underline;
}

.labels-list.inline {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.label-pill {
  display: inline-block;
  font-family: monospace;
  font-size: 0.75rem;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  word-break: break-all;
}
</style>
